import React, { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Waves, 
  Fish, 
  Shield, 
  Users, 
  GraduationCap, 
  Eye,
  Heart,
  Mail,
  MapPin,
  ExternalLink,
  ChevronDown,
  Anchor,
  TreePine,
  AlertTriangle,
  CreditCard,
  CheckCircle,
  Building2,
  TrendingUp,
  Megaphone,
  Network,
  Thermometer,
  Box,
  Lightbulb,
  Settings,
  Scale
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const [volunteerForm, setVolunteerForm] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [volunteerDialogOpen, setVolunteerDialogOpen] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/volunteers`, volunteerForm);
      setSubmitSuccess(true);
      setVolunteerForm({ name: '', email: '', phone: '' });
      
      setTimeout(() => {
        setVolunteerDialogOpen(false);
        setSubmitSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting volunteer form:', error);
      alert('There was an error submitting your registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const programs = [
    {
      icon: <Building2 className="w-12 h-12" />,
      title: 'Infrastructure & Technology',
      focus: 'Blue Hubs',
      description: 'Closing the gap by establishing "Blue Hubs"—centralized landing sites equipped with ice production, hygienic processing areas, and digital traceability tools to monitor catch data.',
      impact: 'Enhanced post-harvest quality and data-driven fisheries management',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Cooperative Empowerment',
      focus: 'Community Transformation',
      description: 'Transforming informal fishing groups into legally recognized, bankable cooperatives capable of managing collective infrastructure and accessing credit.',
      impact: 'Financially independent cooperatives with legal recognition and creditworthiness',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      icon: <Megaphone className="w-12 h-12" />,
      title: 'Advocacy & Policy',
      focus: 'Rights-Based Fisheries',
      description: 'Training cooperative leaders in "Rights-Based Fisheries" to advocate for protected near-shore zones and fair-trade policies at the local and national levels.',
      impact: 'Protected fishing zones and fair-trade policies benefiting local communities',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: <Network className="w-12 h-12" />,
      title: 'Value Chain Efficiency',
      focus: 'Market Linkages',
      description: 'Linking infrastructure to markets by reducing the "middleman" dependency, ensuring that the value added through better cooling and processing stays in the hands of the community.',
      impact: 'Direct market access with increased community value retention',
      color: 'from-indigo-500 to-purple-600'
    }
  ];

  const statistics = [
    { value: '3,333 km', label: 'Coastline Protected', icon: <Waves className="w-8 h-8" /> },
    { value: '4', label: 'Active Programs', icon: <Shield className="w-8 h-8" /> },
    { value: '∞', label: 'Marine Species Saved', icon: <Fish className="w-8 h-8" /> }
  ];

  const threats = [
    {
      icon: <Thermometer className="w-6 h-6" />,
      title: 'The Critical Post-Harvest Loss',
      subtitle: 'Economic leakage between boat and plate',
      points: [
        {
          title: 'Thermal Degradation',
          description: 'Due to the lack of onboard chilling and shore-side cold storage, an estimated 30% to 50% of the catch is lost or downgraded to low-value "trash fish" before it ever reaches a market.'
        },
        {
          title: 'Bacterial Contamination',
          description: 'Without hygienic landing sites, fish are often handled on open sand, leading to rapid spoilage and making the product ineligible for high-value export markets that require strict food safety certifications.'
        }
      ]
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: 'The Chronic Infrastructure Deficit',
      subtitle: "Unlocking Africa's longest coastline",
      points: [
        {
          title: 'Landing Site Paralysis',
          description: 'Most artisanal fishers operate from open beaches without jetties or breakwaters. This limits operations to calm weather and leads to frequent vessel damage during landing.'
        },
        {
          title: 'Energy Poverty',
          description: 'The absence of reliable, affordable electricity in remote coastal villages makes the production of ice—the lifeblood of any fishery—prohibitively expensive or physically impossible.'
        },
        {
          title: 'Logistical Isolation',
          description: 'Poor road networks connecting coastal "Blue Zones" to inland urban hubs create a "dead zone" where fish cannot be transported without spoiling, limiting the market to immediate local consumption.'
        }
      ]
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: 'The Human Capital & Skills Deficit',
      subtitle: 'Knowledge wall preventing Blue Economy transition',
      points: [
        {
          title: 'Informality Trap',
          description: 'Fisheries cooperatives often lack the organizational structure, digital bookkeeping skills, and financial literacy required to become "bankable" entities or manage large-scale assets.'
        },
        {
          title: 'Technical Stagnation',
          description: 'There is a widespread lack of training in modern food-handling protocols (HACCP), engine maintenance, and sustainable harvesting techniques, leaving the workforce ill-equipped for a modern industrial shift.'
        },
        {
          title: 'Leadership Gaps',
          description: 'Limited capacity for evidence-based advocacy means that fishing communities are often excluded from high-level policy discussions regarding licensing and resource rights.'
        }
      ]
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: 'Gear Obsolescence & Technological Exclusion',
      subtitle: 'Competing with modern fleets using archaic tools',
      points: [
        {
          title: 'Nearshore Depletion',
          description: 'Due to inadequate boats and lack of navigation gear (GPS/Sonar), small-scale fishers are restricted to shallow waters. This creates intense competition and overfishing in reef zones while deep-sea pelagic stocks remain untouched by locals.'
        },
        {
          title: 'Safety & Range Restrictions',
          description: 'The use of small, uninsulated vessels without safety equipment or reliable motors prevents fishers from staying at sea long enough to target high-value species like Yellowfin Tuna.'
        },
        {
          title: 'The "Silent" Invasion (IUU)',
          description: 'Without technology to monitor or report encroaching foreign vessels, local fishers are left defenseless against Illegal, Unreported, and Unregulated (IUU) fishing that destroys their nets and depletes their stocks.'
        }
      ]
    },
    {
      icon: <Scale className="w-6 h-6" />,
      title: 'The Governance & Advocacy Vacuum',
      subtitle: 'Policy and representation challenges',
      points: [
        {
          title: 'Jurisdictional Friction',
          description: 'Ongoing ambiguity between Federal and State-level fisheries management creates a "double-taxing" environment and licensing confusion that discourages formal investment.'
        },
        {
          title: 'Invisible Stakeholders',
          description: 'Small-scale fishers currently lack a unified "voice" to advocate for protected nearshore zones (IEZs), leaving them vulnerable to displacement by industrial interests or maritime insecurity.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-md shadow-sm z-50 transition-all">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_marine-advocacy/artifacts/cxplfsqp_image.png?v=20251129" 
                alt="HAMA Logo" 
                className="h-14 w-14 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-800">HAMA</h1>
                <p className="text-xs text-slate-600">Marine Advocacy</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('mission')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Mission</button>
              <button onClick={() => scrollToSection('programs')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Programs</button>
              <button onClick={() => scrollToSection('why-us')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Why Us</button>
              <button onClick={() => scrollToSection('approach')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Approach</button>
              <button onClick={() => scrollToSection('conservation')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Conservation</button>
              <button onClick={() => scrollToSection('trustees')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Board</button>
              <button onClick={() => scrollToSection('impact')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Impact</button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-700 hover:text-cyan-600 transition-colors font-medium">Contact</button>
              <Button onClick={() => scrollToSection('involved')} className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700">
                Get Involved
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/70 via-cyan-900/60 to-slate-900/80 z-10"></div>
          <img 
            src="https://customer-assets.emergentagent.com/job_daf8bd5a-9212-4411-b37b-b26f4ed9613c/artifacts/r3aqkpea_image.jpeg" 
            alt="Ocean Conservation" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className={`relative z-20 text-center px-6 max-w-5xl transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Badge className="mb-6 bg-cyan-500/20 text-cyan-100 border-cyan-400/30 px-6 py-3 text-xl font-semibold">
            <Waves className="w-6 h-6 mr-2" />
            Protecting Africa's Longest Coastline
          </Badge>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Safeguarding Somalia's
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              Marine Ecosystems
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
            Combating illegal fishing, protecting vulnerable species, and empowering coastal communities across the Horn of Africa
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={() => scrollToSection('programs')} 
              size="lg" 
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-6 text-lg shadow-xl"
              data-testid="explore-programs-btn"
            >
              Explore Our Programs
              <ChevronDown className="ml-2 w-5 h-5 animate-bounce" />
            </Button>
            <Button 
              onClick={() => scrollToSection('involved')} 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-cyan-600 px-8 py-6 text-lg backdrop-blur-sm"
              data-testid="get-involved-btn"
            >
              <Heart className="mr-2 w-5 h-5" />
              Get Involved
            </Button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/70" />
        </div>
      </section>

      {/* Mission & Context Section */}
      <section id="mission" className="py-20 bg-white" data-testid="mission-section">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              Our mission is to unlock the potential of Somalia's oceans by dismantling the barriers to prosperity: closing the infrastructure gap, professionalizing the fishing value chain through cooperative support, and securing the rights of local fishers through systemic advocacy and governance reform.
            </p>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-slate-800 mb-6 text-center">Critical Challenges Facing Somalia's Fisheries</h3>
              <p className="text-slate-600 leading-relaxed text-center max-w-3xl mx-auto mb-12">
                Somalia possesses the longest coastline on mainland Africa at <strong>3,333 kilometers</strong> and a significant Exclusive Economic Zone (EEZ). However, multiple systemic barriers prevent coastal communities from realizing the full potential of these marine resources.
              </p>
            </div>
            
            <div className="space-y-6">
              {threats.map((threat, index) => (
                <Card key={index} className="border-l-4 border-l-red-500 hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 text-red-600">
                        {threat.icon}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-1">{index + 1}. {threat.title}</CardTitle>
                        <CardDescription className="text-base italic">{threat.subtitle}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {threat.points.map((point, pointIndex) => (
                        <div key={pointIndex} className="pl-4 border-l-2 border-red-200">
                          <h5 className="font-semibold text-slate-800 mb-1">• {point.title}</h5>
                          <p className="text-sm text-slate-600 leading-relaxed">{point.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center mt-16">
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-slate-800 mb-3">Our Response</h3>
                <p className="text-slate-600 leading-relaxed">
                  HAMA addresses these challenges through a comprehensive, multi-pillar approach that combines infrastructure development, cooperative empowerment, policy advocacy, and value chain optimization to transform Somalia's fishing communities.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_daf8bd5a-9212-4411-b37b-b26f4ed9613c/artifacts/qj9q6y62_image.png" 
                  alt="Horn of Africa Map" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-6 rounded-2xl shadow-xl">
                <p className="text-3xl font-bold">3,333 km</p>
                <p className="text-sm">of coastline to protect</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="programs-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 border-cyan-300">
              Key Pillars
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Four Pillars of Sustainable Fisheries
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Building resilient fishing communities through infrastructure, empowerment, advocacy, and market access
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {programs.map((program, index) => (
              <Card 
                key={index} 
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border-0"
                data-testid={`program-card-${index}`}
              >
                <div className={`h-2 bg-gradient-to-r ${program.color}`}></div>
                <CardHeader>
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${program.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                    {program.icon}
                  </div>
                  <CardTitle className="text-2xl mb-2">{program.title}</CardTitle>
                  <Badge variant="secondary" className="w-fit">{program.focus}</Badge>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CardDescription className="text-base leading-relaxed">
                    {program.description}
                  </CardDescription>
                  <Separator />
                  <div className="flex items-start space-x-2">
                    <Shield className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-slate-600">
                      <strong className="text-green-700">Impact:</strong> {program.impact}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section id="impact" className="py-20 bg-gradient-to-r from-cyan-600 to-blue-700" data-testid="impact-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Reach & Impact
            </h2>
            <p className="text-xl text-blue-100">
              Making a measurable difference across the Horn of Africa
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {statistics.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-colors">
                  <div className="text-white mb-3 flex justify-center">
                    {stat.icon}
                  </div>
                  <p className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</p>
                  <p className="text-blue-100 font-medium">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section id="why-us" className="py-20 bg-white" data-testid="why-us-section">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-cyan-100 text-cyan-700 border-cyan-300">
                Why Choose HAMA
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                Our Unique Approach
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Science-based, localized solutions tailored to Somalia's unique political and environmental context
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="border-l-4 border-l-cyan-500 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">Grassroots + Institutional Integration</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 leading-relaxed">
                    We integrate grassroots community empowerment with institutional lobbying, ensuring conservation efforts are both culturally sensitive and successfully incorporated into national policy.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-blue-500 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">Science-Based Solutions</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 leading-relaxed">
                    Our approach uses scientific evidence to inform strategies, adapted to the specific local conditions and needs of Somalia's coastal communities.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-teal-500 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">Geographical and Local Advantages</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 leading-relaxed mb-3">
                    As a local non-governmental organisation operating within Somalia and the wider Horn of Africa region, HAMA possesses significant on-the-ground advantages that set us apart:
                  </p>
                  <div className="bg-teal-50 rounded-lg p-4 border-l-2 border-teal-400">
                    <p className="text-sm text-slate-700">
                      <strong className="text-teal-700">Deep Contextual Understanding:</strong> Our established local presence provides us with an in-depth understanding of the region's political, social, and environmental dynamics. This enables us to deliver solutions and advocacy that are both culturally sensitive and contextually relevant.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-indigo-500 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Waves className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">Critical Ecosystem Focus</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 leading-relaxed">
                    Dedicated to protecting and restoring Somalia's critical coral reef and seabed ecosystems, securing the future of both the ocean and dependent communities.
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Our Holistic Approach</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="font-semibold text-slate-800 mb-2">Advocacy & Policy</h4>
                    <p className="text-sm text-slate-600">High-level lobbying for sustainable regulations and enforcement</p>
                  </div>
                  <div>
                    <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <GraduationCap className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="font-semibold text-slate-800 mb-2">Education & Awareness</h4>
                    <p className="text-sm text-slate-600">Community training and ocean literacy programs</p>
                  </div>
                  <div>
                    <div className="w-16 h-16 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Waves className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="font-semibold text-slate-800 mb-2">Ecosystem Health</h4>
                    <p className="text-sm text-slate-600">Habitat restoration and pollution control initiatives</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section id="approach" className="py-20 bg-white" data-testid="approach-section">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-300">
                Our Approach
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                Empowering Communities, Securing Rights
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                We combine technical resilience with rights-based advocacy to create lasting change for coastal communities
              </p>
            </div>

            {/* Two Main Approaches */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {/* Resilience Approach */}
              <Card className="border-t-4 border-t-blue-600 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Shield className="w-6 h-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-2xl">The "Resilience" Approach</CardTitle>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                    Technical & Structural
                  </Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 leading-relaxed">
                    Our initiative centers on empowering small-scale fishers by fostering robust, independent cooperatives that serve as the backbone of local fisheries governance. Through targeted capacity development, we equip these groups with the technical and financial literacy needed to manage collective assets and reduce post-harvest losses. Simultaneously, we lead strategic advocacy efforts to ensure cooperative voices are integrated into national policy dialogues, transforming individual fishers from passive participants into influential stakeholders who command fair prices and secure resource rights.
                  </p>
                </CardContent>
              </Card>

              {/* Community Advocate Approach */}
              <Card className="border-t-4 border-t-cyan-600 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-cyan-600" />
                    </div>
                    <CardTitle className="text-2xl">The "Community Advocate"</CardTitle>
                  </div>
                  <Badge className="bg-cyan-100 text-cyan-700 border-cyan-300">
                    Rights-Based & Local
                  </Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 leading-relaxed">
                    We believe that sustainable oceans begin with secured rights. Our initiative focuses on participatory governance, helping coastal communities formalize their tenure and take a seat at the decision-making table. To turn these rights into lasting prosperity, we provide end-to-end value chain support—from modernizing landing sites to establishing fair-trade linkages. This holistic approach reduces post-harvest losses and guarantees that the people closest to the resource are the ones who benefit most from its value.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Key Components */}
            <Card className="bg-gradient-to-br from-slate-50 to-blue-50 border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-3xl text-center mb-2">Key Components</CardTitle>
                <CardDescription className="text-center text-base">
                  Our comprehensive framework for sustainable marine management
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Governance */}
                  <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Shield className="w-7 h-7 text-white" />
                    </div>
                    <h4 className="font-bold text-slate-800 mb-3 text-center text-lg">Governance</h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start space-x-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Co-management systems</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Policy advocacy</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>IUU surveillance</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>Tenure rights</span>
                      </li>
                    </ul>
                  </div>

                  {/* Value Chain */}
                  <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Fish className="w-7 h-7 text-white" />
                    </div>
                    <h4 className="font-bold text-slate-800 mb-3 text-center text-lg">Value Chain</h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start space-x-2">
                        <span className="text-cyan-600 mt-1">•</span>
                        <span>Post-harvest loss reduction</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-cyan-600 mt-1">•</span>
                        <span>Cold chain logistics</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-cyan-600 mt-1">•</span>
                        <span>Market access</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-cyan-600 mt-1">•</span>
                        <span>Fair Trade & FIPs certification</span>
                      </li>
                    </ul>
                  </div>

                  {/* Cross-cutting */}
                  <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-7 h-7 text-white" />
                    </div>
                    <h4 className="font-bold text-slate-800 mb-3 text-center text-lg">Cross-cutting</h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start space-x-2">
                        <span className="text-indigo-600 mt-1">•</span>
                        <span>Gender equity in processing</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-indigo-600 mt-1">•</span>
                        <span>Climate resilience</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-indigo-600 mt-1">•</span>
                        <span>Financial inclusion</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Marine Conservation Programme Section */}
      <section id="conservation" className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50" data-testid="conservation-section">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-300">
                Marine Conservation Programme
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                Safeguarding Marine Ecosystems
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Our programme aims to safeguard and restore vital marine habitats, while implementing effective pollution control measures to ensure the long-term health of coastal ecosystems.
              </p>
            </div>

            {/* Habitat Restoration */}
            <div className="mb-16">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center">
                  <Waves className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-slate-800">Habitat Restoration</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Coral Reef Rehabilitation */}
                <Card className="border-t-4 border-t-teal-500 hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                        <Fish className="w-6 h-6 text-teal-600" />
                      </div>
                      <CardTitle className="text-xl">Coral Reef Rehabilitation</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-slate-600 leading-relaxed">
                      The programme will establish dedicated coral nurseries to cultivate and transplant resilient coral species. These efforts are designed to support the recovery of damaged reefs and enhance their resilience to environmental stressors.
                    </p>
                    <div className="bg-teal-50 rounded-lg p-4 border-l-2 border-teal-400">
                      <p className="text-sm text-slate-700">
                        <strong className="text-teal-700">Community Training:</strong> Divers and local community members will receive training in advanced reef restoration techniques, empowering them to participate actively in rehabilitation initiatives.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Mangrove and Seagrass Restoration */}
                <Card className="border-t-4 border-t-green-500 hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <TreePine className="w-6 h-6 text-green-600" />
                      </div>
                      <CardTitle className="text-xl">Mangrove and Seagrass Restoration</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-slate-600 leading-relaxed">
                      To reinforce natural coastal defences, degraded areas along the shore will be replanted with native mangrove and seagrass species. These restoration activities play a crucial role in protecting against erosion and storm surges.
                    </p>
                    <div className="bg-green-50 rounded-lg p-4 border-l-2 border-green-400">
                      <p className="text-sm text-slate-700">
                        <strong className="text-green-700">Blue Carbon Projects:</strong> The programme will promote initiatives that capture and store atmospheric carbon, thereby contributing to climate mitigation efforts.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Pollution Control */}
            <div>
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-slate-800">Pollution Control</h3>
              </div>

              <Card className="border-t-4 border-t-orange-500 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-orange-600" />
                    </div>
                    <CardTitle className="text-xl">Plastic Reduction</CardTitle>
                  </div>
                  <CardDescription>Tackling plastic pollution as a key priority</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-white text-xs font-bold">1</span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-slate-800 mb-1">Business Partnerships</h5>
                        <p className="text-sm text-slate-600">
                          Partner with local businesses to gradually phase out single-use plastics, reducing the volume of waste entering the marine environment.
                        </p>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-white text-xs font-bold">2</span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-slate-800 mb-1">Community Recycling Hubs</h5>
                        <p className="text-sm text-slate-600">
                          Launch dedicated recycling centers complemented by regular ocean cleanup drives to enhance local participation and ensure effective waste management.
                        </p>
                      </div>
                    </div>

                    <div className="bg-orange-50 rounded-lg p-4 border-l-2 border-orange-400 mt-4">
                      <p className="text-sm text-slate-700 font-medium">
                        💡 These initiatives work together to create a comprehensive approach to reducing plastic pollution and protecting our marine ecosystems.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Board of Trustees Section */}
      <section id="trustees" className="py-20 bg-white" data-testid="trustees-section">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-indigo-100 text-indigo-700 border-indigo-300">
                Leadership
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                Board of Trustees
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Guiding HAMA's mission with expertise, dedication, and a commitment to protecting Somalia's marine ecosystems
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Omar Abdrizak - Chair */}
              <Card className="border-t-4 border-t-indigo-600 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-full mx-auto mb-4">
                    <Users className="w-10 h-10 text-indigo-600" />
                  </div>
                  <CardTitle className="text-center text-xl">Omar Abdrizak</CardTitle>
                  <Badge className="mx-auto bg-indigo-600 text-white">Chair of Trustees</Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-indigo-700">Expertise:</strong> Programme management, operations, finance, and administration
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Omar has contributed over 15 years of dedicated service to the Food and Agriculture Organization (FAO), bringing broad leadership experience in programme management, operations, finance, and administration. He has worked with various FAO departments, strengthening organizational systems, enhancing operational effectiveness, and supporting the implementation of complex development programmes.
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    As Trustee Chairperson, Omar pairs strategic guidance with a strong commitment to accountability, transparency, and good governance. His diverse expertise allows him to lead boards decisively, build robust partnerships, and ensure that organizational objectives are achieved with integrity and meaningful impact.
                  </p>
                </CardContent>
              </Card>

              {/* Dr. Abdulaziz */}
              <Card className="border-t-4 border-t-cyan-600 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-center w-20 h-20 bg-cyan-100 rounded-full mx-auto mb-4">
                    <Fish className="w-10 h-10 text-cyan-600" />
                  </div>
                  <CardTitle className="text-center text-xl">Dr. Abdulaziz</CardTitle>
                  <Badge className="mx-auto bg-cyan-600 text-white">Trustee - Scientific Expert</Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-cyan-700">Expertise:</strong> Marine biology, coral reef resilience, habitat restoration
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    PhD in Fisheries. Leading marine biologist specializing in coral reefs and seagrass beds across the Western Indian Ocean. Leads the "Net Zero Trawl" campaign with robust scientific evidence.
                  </p>
                </CardContent>
              </Card>

              {/* Sahra Hassan */}
              <Card className="border-t-4 border-t-blue-600 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mx-auto mb-4">
                    <Shield className="w-10 h-10 text-blue-600" />
                  </div>
                  <CardTitle className="text-center text-xl">Sahra Hassan</CardTitle>
                  <Badge className="mx-auto bg-blue-600 text-white">Trustee - Policy & Governance</Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-blue-700">Expertise:</strong> Regional affairs, environmental policy, government relations
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Background in regional affairs and environmental ministry. Experience in federal and regional government. Translates HAMA's advocacy into tangible national policy.
                  </p>
                </CardContent>
              </Card>

              {/* Eng. Ahmed Abdi Aden */}
              <Card className="border-t-4 border-t-teal-600 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-center w-20 h-20 bg-teal-100 rounded-full mx-auto mb-4">
                    <Waves className="w-10 h-10 text-teal-600" />
                  </div>
                  <CardTitle className="text-center text-xl">Eng. Ahmed Abdi Aden</CardTitle>
                  <Badge className="mx-auto bg-teal-600 text-white">Trustee - Aquaculture</Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-teal-700">Expertise:</strong> Aquaculture, sustainable marine resource management
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Technical specialist in aquaculture. Guides HAMA's strategies for food security, economic development, and sustainable use of coastal resources with innovative, science-driven approaches.
                  </p>
                </CardContent>
              </Card>

              {/* Ambassador Abukar Osman */}
              <Card className="border-t-4 border-t-purple-600 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mx-auto mb-4">
                    <ExternalLink className="w-10 h-10 text-purple-600" />
                  </div>
                  <CardTitle className="text-center text-xl">Ambassador Abukar Osman</CardTitle>
                  <Badge className="mx-auto bg-purple-600 text-white">Trustee - International Policy</Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-purple-700">Expertise:</strong> Diplomacy, international relations, donor engagement
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    40+ years in diplomacy. Former Minister for Livestock, Parliamentarian, and Ambassador to Indonesia and Malaysia. Excels in high-level advocacy with international donors and NGOs.
                  </p>
                </CardContent>
              </Card>

              {/* Mr. Hashim Abanor */}
              <Card className="border-t-4 border-t-green-600 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mx-auto mb-4">
                    <Users className="w-10 h-10 text-green-600" />
                  </div>
                  <CardTitle className="text-center text-xl">Mr. Hashim Abanor</CardTitle>
                  <Badge className="mx-auto bg-green-600 text-white">Trustee - Community Engagement</Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-green-700">Expertise:</strong> Fishing cooperatives, grassroots mobilization
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Experience with fishing cooperatives. Provides a grassroots perspective, ensuring HAMA's initiatives are culturally sensitive and tailored to local artisanal fishers' needs.
                  </p>
                </CardContent>
              </Card>

              {/* Faduma Ibrahim */}
              <Card className="border-t-4 border-t-pink-600 hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-center w-20 h-20 bg-pink-100 rounded-full mx-auto mb-4">
                    <Mail className="w-10 h-10 text-pink-600" />
                  </div>
                  <CardTitle className="text-center text-xl">Faduma Ibrahim</CardTitle>
                  <Badge className="mx-auto bg-pink-600 text-white">Trustee - Communications</Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-pink-700">Expertise:</strong> Strategic communications, advocacy, outreach
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Shapes HAMA's public narrative. Translates complex scientific data into accessible content, drives awareness for marine conservation, and maximizes global visibility for HAMA's mission.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Get Involved Section */}
      <section id="involved" className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="get-involved-section">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                Join the Movement
              </h2>
              <p className="text-xl text-slate-600">
                Together, we can protect Somalia's marine heritage for future generations
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="text-center hover:shadow-xl transition-shadow border-t-4 border-t-cyan-500">
                <CardHeader>
                  <div className="mx-auto w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-8 h-8 text-cyan-600" />
                  </div>
                  <CardTitle>Volunteer</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4">
                    Join our community-led programs and make a direct impact on the ground
                  </p>
                  <Dialog open={volunteerDialogOpen} onOpenChange={setVolunteerDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-cyan-600 hover:bg-cyan-700 w-full" data-testid="volunteer-register-btn">
                        Register Now
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Volunteer Registration</DialogTitle>
                        <DialogDescription>
                          Fill out the form below and we'll contact you about volunteer opportunities.
                        </DialogDescription>
                      </DialogHeader>
                      {submitSuccess ? (
                        <div className="flex flex-col items-center justify-center py-8">
                          <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                          <h3 className="text-xl font-semibold text-green-700 mb-2">Thank You!</h3>
                          <p className="text-slate-600 text-center">We'll be in touch soon.</p>
                        </div>
                      ) : (
                        <form onSubmit={handleVolunteerSubmit} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="name">Full Name *</Label>
                            <Input
                              id="name"
                              type="text"
                              placeholder="Enter your full name"
                              value={volunteerForm.name}
                              onChange={(e) => setVolunteerForm({...volunteerForm, name: e.target.value})}
                              required
                              data-testid="volunteer-name-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder="your.email@example.com"
                              value={volunteerForm.email}
                              onChange={(e) => setVolunteerForm({...volunteerForm, email: e.target.value})}
                              required
                              data-testid="volunteer-email-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone Number *</Label>
                            <Input
                              id="phone"
                              type="tel"
                              placeholder="+252 xx xxx xxxx"
                              value={volunteerForm.phone}
                              onChange={(e) => setVolunteerForm({...volunteerForm, phone: e.target.value})}
                              required
                              data-testid="volunteer-phone-input"
                            />
                          </div>
                          <Button 
                            type="submit" 
                            className="w-full bg-cyan-600 hover:bg-cyan-700" 
                            disabled={isSubmitting}
                            data-testid="volunteer-submit-btn"
                          >
                            {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                          </Button>
                        </form>
                      )}
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-xl transition-shadow border-t-4 border-t-blue-500">
                <CardHeader>
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <Heart className="w-8 h-8 text-blue-600" />
                  </div>
                  <CardTitle>Support</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4">
                    Help fund our programs and conservation initiatives across Somalia
                  </p>
                  <div className="space-y-2">
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() => window.open('https://www.paypal.com/donate', '_blank')}
                      data-testid="paypal-donate-btn"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Donate via PayPal
                    </Button>
                    <Button 
                      className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                      onClick={() => window.open('https://donate.stripe.com', '_blank')}
                      data-testid="card-donate-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Donate via Card
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-xl transition-shadow border-t-4 border-t-indigo-500">
                <CardHeader>
                  <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                    <Waves className="w-8 h-8 text-indigo-600" />
                  </div>
                  <CardTitle>Spread Awareness</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4">
                    Share our mission and educate others about marine conservation
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-slate-800 to-slate-900 text-white" data-testid="contact-section">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-4xl font-bold mb-6">Get in Touch</h2>
                <p className="text-slate-300 mb-8 leading-relaxed">
                  Have questions or want to learn more about our work? We'd love to hear from you.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Mail className="w-6 h-6 text-cyan-400 mt-1" />
                    <div>
                      <p className="font-semibold">Email</p>
                      <a href="mailto:info@hornmarineadvocacy.org" className="text-cyan-400 hover:text-cyan-300">
                        info@hornmarineadvocacy.org
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <ExternalLink className="w-6 h-6 text-cyan-400 mt-1" />
                    <div>
                      <p className="font-semibold">Website</p>
                      <a href="https://www.hornmarineadvocacy.org" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">
                        www.hornmarineadvocacy.org
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-6 h-6 text-cyan-400 mt-1" />
                    <div>
                      <p className="font-semibold">Location</p>
                      <p className="text-slate-300">Mogadishu, Somalia</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-700/50 backdrop-blur-sm rounded-2xl p-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marine-advocacy/artifacts/cxplfsqp_image.png?v=20251129" 
                  alt="HAMA Logo" 
                  className="w-48 h-48 mx-auto mb-6 object-contain"
                />
                <h3 className="text-2xl font-bold text-center mb-4">
                  Horn of Africa Marine Advocacy
                </h3>
                <p className="text-center text-slate-300">
                  Protecting marine ecosystems and empowering coastal communities
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="font-bold text-lg mb-4">About HAMA</h4>
              <p className="text-slate-400 text-sm leading-relaxed">
                Safeguarding Somalia's vital marine ecosystems through advocacy, awareness, and local action.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('mission')} className="text-slate-400 hover:text-cyan-400 transition-colors">Mission</button></li>
                <li><button onClick={() => scrollToSection('programs')} className="text-slate-400 hover:text-cyan-400 transition-colors">Programs</button></li>
                <li><button onClick={() => scrollToSection('why-us')} className="text-slate-400 hover:text-cyan-400 transition-colors">Why Us</button></li>
                <li><button onClick={() => scrollToSection('approach')} className="text-slate-400 hover:text-cyan-400 transition-colors">Approach</button></li>
                <li><button onClick={() => scrollToSection('conservation')} className="text-slate-400 hover:text-cyan-400 transition-colors">Conservation</button></li>
                <li><button onClick={() => scrollToSection('trustees')} className="text-slate-400 hover:text-cyan-400 transition-colors">Board</button></li>
                <li><button onClick={() => scrollToSection('impact')} className="text-slate-400 hover:text-cyan-400 transition-colors">Impact</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-slate-400 hover:text-cyan-400 transition-colors">Contact</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4">Connect With Us</h4>
              <div className="flex space-x-4">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-cyan-600 transition-colors" aria-label="Facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://twitter.com/HornmAdvocacy" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-cyan-600 transition-colors" aria-label="Twitter">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-cyan-600 transition-colors" aria-label="LinkedIn">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-cyan-600 transition-colors" aria-label="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/></svg>
                </a>
              </div>
            </div>
          </div>
          
          <Separator className="mb-6 bg-slate-700" />
          
          <div className="text-center text-slate-400 text-sm">
            <p>&copy; {new Date().getFullYear()} Horn of Africa Marine Advocacy (HAMA). All rights reserved.</p>
            <p className="mt-2">Protecting Somalia's marine heritage for future generations</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;